import React, { useState, useEffect } from 'react';
import {
  DownloadCloud,
  Smartphone,
  RefreshCw,
  Save,
  CheckCircle2,
  Sliders,
  Sparkles
} from 'lucide-react';
import {
  CURRENT_CLIENT_VERSION,
  subscribeVersionConfig,
  saveVersionConfig
} from './versionConfig';

export default function VersionManagementSuperadminModule({
  user,
  db,
  isFirebaseActive
}) {
  const [config, setConfig] = useState({
    minVersion: CURRENT_CLIENT_VERSION,
    isForceUpdateActive: false,
    title: 'Önemli Sistem ve Bildirim Güncellemesi',
    message: 'Yeni sürümümüzde anlık push bildirimleri ve yapay zeka hızlandırmaları eklenmiştir. Kesintisiz koçluk deneyimi için lütfen güncelleyiniz.',
    downloadUrl: '',
    updatedAt: new Date().toISOString()
  });

  const [isSaving, setIsSaving] = useState(false);

  // Sürüm yapılandırmasını dinle
  useEffect(() => {
    const unsub = subscribeVersionConfig(db, isFirebaseActive, (data) => {
      setConfig(prev => ({
        ...prev,
        ...data
      }));
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [db, isFirebaseActive]);

  // Form Değişiklikleri
  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Hızlıca Mevcut Sürümü Minimum Yap
  const setMinToCurrent = () => {
    setConfig(prev => ({
      ...prev,
      minVersion: CURRENT_CLIENT_VERSION,
      isForceUpdateActive: true
    }));
  };

  // Kaydet
  const handleSave = async (e) => {
    e.preventDefault();
    if (!config.minVersion.trim()) {
      if (window.toast) window.toast.warning('Lütfen bir sürüm numarası giriniz (Örn: 1.1.0)');
      return;
    }

    setIsSaving(true);
    try {
      await saveVersionConfig({
        minVersion: config.minVersion,
        isForceUpdateActive: config.isForceUpdateActive,
        title: config.title,
        message: config.message,
        downloadUrl: config.downloadUrl,
        db,
        isFirebaseActive
      });

      if (window.toast) {
        window.toast.success('Sürüm ayarları kaydedildi ve tüm kullanıcılarda anında aktif oldu!');
      }
    } catch (err) {
      console.error("Sürüm kaydetme hatası:", err);
      if (window.toast) window.toast.error('Sürüm ayarları kaydedilemedi.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-8 custom-scrollbar">
      
      {/* 1. ÜST BAŞLIK */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-tr from-orange-600 to-amber-500 rounded-2xl shadow-lg shadow-orange-500/20 text-white">
            <DownloadCloud size={26} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                Sürüm Yönetimi & Zorunlu Güncelleme
              </h1>
              <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-orange-500/20 border border-orange-500/40 text-orange-400">
                Superadmin
              </span>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              En düşük zorunlu uygulama sürümünü belirleyin. Eski sürümde kalan kullanıcıların ekranı otomatik kilitlenir.
            </p>
          </div>
        </div>

        {/* Durum Rozetleri */}
        <div className="flex items-center gap-3">
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-3 px-4 text-center">
            <div className="text-[10px] text-slate-400 font-medium">Bu Kod Paketi</div>
            <div className="text-lg font-black text-emerald-400">v{CURRENT_CLIENT_VERSION}</div>
          </div>

          <div className={`p-3 px-4 rounded-2xl border text-center ${
            config.isForceUpdateActive
              ? 'bg-rose-500/10 border-rose-500/30 text-rose-400'
              : 'bg-slate-900/80 border-slate-800 text-slate-400'
          }`}>
            <div className="text-[10px] font-medium">Zorunlu Kilit</div>
            <div className="text-lg font-black">{config.isForceUpdateActive ? 'AKTİF' : 'PASİF'}</div>
          </div>
        </div>
      </div>

      {/* 2. FORM VE CANLI TELEFON ÖNİZLEMESİ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* SOL: AYARLAR FORMU (7 Kolon) */}
        <div className="lg:col-span-7 bg-slate-900/90 border border-slate-800 rounded-3xl p-6 sm:p-7 shadow-xl space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sliders size={18} className="text-orange-400" />
              <h2 className="text-lg font-bold text-white">Sürüm Yapılandırması</h2>
            </div>
            <button
              type="button"
              onClick={setMinToCurrent}
              className="text-xs px-3 py-1.5 rounded-xl bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30 font-bold transition-all flex items-center gap-1.5"
            >
              <Sparkles size={13} />
              <span>v{CURRENT_CLIENT_VERSION} Sürümünü Şart Koş</span>
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            
            {/* A. ZORUNLU GÜNCELLEME AÇIK/KAPALI ANAHTARI */}
            <div className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-4 ${
              config.isForceUpdateActive
                ? 'bg-orange-500/10 border-orange-500/40'
                : 'bg-slate-950/60 border-slate-800'
            }`}>
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-white">Zorunlu Güncelleme Kilidi</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                    config.isForceUpdateActive ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' : 'bg-slate-800 text-slate-400 border-slate-700'
                  }`}>
                    {config.isForceUpdateActive ? 'Kilit Açık' : 'Devre Dışı'}
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  {config.isForceUpdateActive
                    ? 'Açık: Belirttiğiniz sürümden eski kullanıcılar uygulamayı kullanamaz, güncellemeye zorlanır.'
                    : 'Kapalı: Eski sürümdeki kullanıcılar uygulamayı normal kullanmaya devam eder.'}
                </p>
              </div>

              {/* Toggle Switch */}
              <button
                type="button"
                onClick={() => handleChange('isForceUpdateActive', !config.isForceUpdateActive)}
                className={`w-14 h-8 flex items-center rounded-full p-1 transition-colors duration-300 shrink-0 ${
                  config.isForceUpdateActive ? 'bg-orange-600 justify-end' : 'bg-slate-800 justify-start'
                }`}
              >
                <div className="bg-white w-6 h-6 rounded-full shadow-md transform transition-transform" />
              </button>
            </div>

            {/* B. MİNİMUM GEREKEN SÜRÜM */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Minimum Zorunlu Sürüm (SemVer) <span className="text-orange-500">*</span>
                </label>
                <span className="text-[11px] text-slate-500">Örn: 1.1.0 veya 1.2.0</span>
              </div>
              <input
                type="text"
                value={config.minVersion}
                onChange={e => handleChange('minVersion', e.target.value)}
                placeholder="1.1.0"
                className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-sm font-mono font-bold text-orange-400 placeholder-slate-500 focus:outline-none focus:border-orange-500 transition-colors"
                maxLength={20}
              />
            </div>

            {/* C. GÜNCELLEME BAŞLIĞI */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                Kilit Ekranı Başlığı <span className="text-orange-500">*</span>
              </label>
              <input
                type="text"
                value={config.title}
                onChange={e => handleChange('title', e.target.value)}
                placeholder="Örn: Yeni Sürüm Yayınlandı!"
                className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 transition-colors"
                maxLength={80}
              />
            </div>

            {/* D. GÜNCELLEME AÇIKLAMASI */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Güncelleme Notları / Kullanıcıya Gösterilecek Mesaj <span className="text-orange-500">*</span>
                </label>
                <span className="text-[11px] text-slate-500">{config.message.length}/300 Karakter</span>
              </div>
              <textarea
                value={config.message}
                onChange={e => handleChange('message', e.target.value)}
                rows={4}
                placeholder="Neden güncellemeleri gerektiğini ve yeni eklenen özellikleri yazın..."
                className="w-full p-4 bg-slate-950 border border-slate-800 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 transition-colors custom-scrollbar leading-relaxed"
                maxLength={300}
              />
            </div>

            {/* E. İNDİRME / MAĞAZA BAĞLANTISI */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                İndirme / Mağaza Bağlantısı (URL)
              </label>
              <input
                type="url"
                value={config.downloadUrl}
                onChange={e => handleChange('downloadUrl', e.target.value)}
                placeholder="https://play.google.com/store/apps/details?id=... (boş bırakılırsa sayfayı yeniler)"
                className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 transition-colors"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                * Kullanıcı "Şimdi Güncelle" butonuna bastığında bu adrese yönlendirilir. Boş bırakılırsa tarayıcı önbelleğini sıfırlar.
              </p>
            </div>

            {/* KAYDET BUTONU */}
            <button
              type="submit"
              disabled={isSaving}
              className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white font-bold text-sm sm:text-base tracking-wide shadow-xl shadow-orange-500/25 transition-all flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  <span>Kaydediliyor ve Yayınlanıyor...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>Sürüm Kuralını Kaydet ve Herkeste Zorunlu Kıl</span>
                </>
              )}
            </button>

          </form>
        </div>

        {/* SAĞ: CANLI TELEFON KİLİT EKRANI ÖNİZLEMESİ (5 Kolon) */}
        <div className="lg:col-span-5 flex flex-col items-center">
          <div className="w-full flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
              <Smartphone size={16} className="text-orange-400" />
              <span>Eski Sürümdeki Kullanıcının Ekranı</span>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
              config.isForceUpdateActive ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' : 'bg-slate-800 text-slate-400 border-slate-700'
            }`}>
              {config.isForceUpdateActive ? 'Kilit Ekranda' : 'Kilit Kapalı'}
            </span>
          </div>

          {/* Telefon Çerçevesi */}
          <div className="relative w-full max-w-[340px] h-[640px] bg-slate-950 rounded-[44px] border-[8px] border-slate-800 shadow-2xl p-4 flex flex-col overflow-hidden ring-1 ring-slate-700">
            
            {/* Telefon Üst Ada */}
            <div className="w-24 h-4 bg-slate-800 rounded-full mx-auto mb-4 shrink-0" />

            {config.isForceUpdateActive ? (
              /* Kilit Ekranı İçeriği */
              <div className="relative z-10 m-auto w-full bg-slate-900 border-2 border-orange-500/40 rounded-2xl shadow-2xl p-4 text-center space-y-4 animate-fade-in">
                
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-orange-600 to-amber-500 text-white flex items-center justify-center mx-auto shadow-lg shadow-orange-500/30">
                  <DownloadCloud size={28} />
                </div>

                <div className="space-y-1">
                  <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/30">
                    ZORUNLU GÜNCELLEME
                  </span>
                  <h3 className="font-bold text-sm text-white leading-tight">
                    {config.title || 'Yeni Sürüm Yayınlandı!'}
                  </h3>
                  <div className="text-[10px] text-slate-400 pt-0.5">
                    Sizin: <b className="text-rose-400">v1.0.0</b> ➔ Zorunlu: <b className="text-orange-400">v{config.minVersion}</b>
                  </div>
                </div>

                <div className="text-xs text-slate-300 leading-relaxed max-h-32 overflow-y-auto bg-slate-950/70 p-2.5 rounded-xl border border-slate-800 text-left">
                  {config.message}
                </div>

                <div className="space-y-1.5 pt-1">
                  <div className="w-full py-2.5 px-3 rounded-xl bg-orange-600 text-white font-black text-xs shadow-md text-center flex items-center justify-center gap-1.5">
                    <DownloadCloud size={14} />
                    <span>Şimdi Yeni Sürümü İndir</span>
                  </div>
                  <div className="w-full py-1.5 px-3 rounded-lg border border-slate-800 bg-slate-800 text-[10px] font-bold text-slate-400 text-center flex items-center justify-center gap-1">
                    <RefreshCw size={11} />
                    <span>Önbelleği Temizle & Yenile</span>
                  </div>
                </div>

              </div>
            ) : (
              /* Kilit Kapalıyken Normal Uygulama Görünümü */
              <div className="m-auto text-center p-6 text-slate-500 text-xs space-y-2">
                <CheckCircle2 size={36} className="mx-auto text-emerald-400 opacity-70" />
                <p className="font-bold text-slate-300 text-sm">Zorunlu Güncelleme Kapalı</p>
                <p className="text-[11px] text-slate-500">
                  Kullanıcılar hangi sürümde olursa olsun uygulamaya serbestçe girebilirler.
                </p>
              </div>
            )}

            {/* Telefon Alt Çizgisi */}
            <div className="w-28 h-1 bg-slate-700 rounded-full mx-auto mt-auto shrink-0" />
          </div>
        </div>

      </div>

    </div>
  );
}
